import React, { useContext, useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { trackSuiteEvent } from "shared/util/track";
import { PLANS, SUBSCRIPTION_STATUS, PLANS_META, STRIPE_SUBSCRIPTION_STATUS } from "@routebot/constants";
import PlanCards from "components/ui/cards/PlanCards";
import AreYouSureModal from "components/shared/AreYouSureModal";
import EmailVerificationBanner from "components/settings/EmailVerificationBanner";

const formatDate = (value) => {
  if (!value) return null;
  const date = typeof value === "string" || typeof value === "number" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const SubscriptionSettings = () => {
  const { t } = useTranslation();
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);

  const [error, setError] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Suite Events — yalnızca routebot (uluslararası) temada çalışır; servistesin'i etkilemez.
  useEffect(() => {
    trackSuiteEvent("Subscription — Page View", "page_view", {
      plan: auth?.company?.plan || null,
      isDemo: !!auth?.company?.isDemo,
    });
  }, [auth?.company?.plan, auth?.company?.isDemo]);

  const planSummary = useMemo(() => {
    const plan = auth?.company?.plan || PLANS.FREE;
    const status = auth?.company?.subscriptionStatus || SUBSCRIPTION_STATUS.ACTIVE;
    const endDate = auth?.company?.currentPeriodEnd || null;
    const cancelAtPeriodEnd = auth?.company?.cancelAtPeriodEnd ?? false;

    return {
      plan,
      status,
      endDate,
      cancelAtPeriodEnd,
      periodEndText: formatDate(endDate),
    };
  }, [
    auth?.company?.plan,
    auth?.company?.currentPeriodEnd,
    auth?.company?.subscriptionStatus,
    auth?.company?.cancelAtPeriodEnd,
  ]);

  const subscriptionState = useMemo(() => {
    const providerStatus = auth?.company?.providerSubscriptionStatus;
    const hasStripeSubscription = !!auth?.company?.providerSubscriptionId;
    const localStatus = auth?.company?.subscriptionStatus;
    const isCanceled =
      localStatus === SUBSCRIPTION_STATUS.CANCELED ||
      providerStatus === STRIPE_SUBSCRIPTION_STATUS.CANCELED ||
      providerStatus === STRIPE_SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED;

    return {
      hasStripeSubscription,
      isCanceled,
      requiresCheckout: !hasStripeSubscription || isCanceled,
      hadSubscribedBefore: !!auth?.company?.providerCustomerId && !!auth?.company?.subscriptionStatus,
    };
  }, [
    auth?.company?.providerSubscriptionId,
    auth?.company?.subscriptionStatus,
    auth?.company?.providerSubscriptionStatus,
  ]);

  const handleOpenPortal = useCallback(async () => {
    setError(null);
    trackSuiteEvent("Subscription — Manage Billing (Portal)", "button_click");
    try {
      const returnUrl = window.location.href;
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/billing/portal`,
        "POST",
        JSON.stringify({ returnUrl }),
        { "Content-Type": "application/json" },
      );

      if (response?.url || response?.portalUrl) {
        window.location.href = response.url || response.portalUrl;
        return;
      }

      throw new Error(t("subscription.portalError") || "Failed to open billing portal");
    } catch (err) {
      setError(err?.message || t("subscription.portalError") || "Failed to open billing portal");
      throw err;
    }
  }, [sendRequest, t]);

  const startCheckoutOrChange = useCallback(
    async (planKey = PLANS.PRO) => {
      setError(null);

      trackSuiteEvent("Subscription — Change/Upgrade Plan", "button_click", {
        planKey,
        requiresCheckout: subscriptionState.requiresCheckout,
      });

      // If user needs checkout (first time or fully canceled subscription)
      if (subscriptionState.requiresCheckout) {
        try {
          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_URL}/billing/checkout`,
            "POST",
            JSON.stringify({ planKey }),
            { "Content-Type": "application/json" },
          );

          if (response?.url) {
            window.location.href = response.url;
          }
        } catch (err) {
          setError(err?.message || "Checkout başlatılamadı");
        }
        return;
      }

      // Active subscription: open Stripe's confirmation for this exact plan.
      // Cancel, invoices, and reactivation keep the plain portal via handleOpenPortal.
      try {
        const returnUrl = window.location.href;
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/billing/portal`,
          "POST",
          JSON.stringify({ returnUrl, planKey }),
          { "Content-Type": "application/json" },
        );

        if (response?.url || response?.portalUrl) {
          window.location.href = response.url || response.portalUrl;
          return;
        }

        throw new Error(t("subscription.portalError") || "Failed to open billing portal");
      } catch (err) {
        setError(err?.message || t("subscription.changeError") || "Failed to open billing portal");
      }
    },
    [sendRequest, subscriptionState.requiresCheckout, t],
  );

  const handleCancelPlan = useCallback(() => {
    trackSuiteEvent("Subscription — Cancel clicked", "button_click");
    setShowCancelModal(true);
  }, []);

  const handleConfirmCancelPlan = useCallback(async () => {
    setShowCancelModal(false);
    setError(null);
    setIsCanceling(true);
    try {
      await handleOpenPortal();
    } catch (err) {
      setError(err?.message || t("subscription.cancelError") || "Failed to cancel subscription");
      setIsCanceling(false);
    }
  }, [handleOpenPortal, t]);

  const handleReactivatePlan = useCallback(async () => {
    trackSuiteEvent("Subscription — Reactivate clicked", "button_click");
    setError(null);
    setIsReactivating(true);
    try {
      await handleOpenPortal();
    } catch (err) {
      setError(err?.message || t("subscription.reactivateError") || "Failed to reactivate subscription");
    } finally {
      setIsReactivating(false);
    }
  }, [handleOpenPortal, t]);

  const isEmailVerified = auth.client?.emailConfirmed;
  const isDemo = auth.company?.isDemo;

  return (
    <div className="centered-form-padding-lg">
      <EmailVerificationBanner />

      <div
        style={
          isEmailVerified && !isDemo
            ? {}
            : {
                opacity: 0.5,
                pointerEvents: "none",
                filter: "grayscale(30%)",
                userSelect: "none",
              }
        }
      >
        <h2 style={{ marginBottom: "4px", marginTop: "12px" }}>{t("subscription.manageTitle")}</h2>
        <p style={{ color: "#6b7280", marginBottom: "24px" }}>{t("subscription.manageDescription")}</p>

        <PlanCards
          currentPlanKey={planSummary.plan}
          plans={PLANS}
          plansMeta={PLANS_META}
          onChangePlan={startCheckoutOrChange}
          onCancelPlan={handleCancelPlan}
          cancelAtPeriodEnd={planSummary.cancelAtPeriodEnd}
          periodEndDate={planSummary.endDate}
          onReactivatePlan={handleReactivatePlan}
          isCanceling={isCanceling}
          isReactivating={isReactivating}
          onOpenPortal={handleOpenPortal}
          hadSubscribedBefore={subscriptionState.hadSubscribedBefore}
        />

        {error && (
          <p style={{ color: "#c62828", marginTop: "12px" }}>
            {t("error") || "Error"}: {error}
          </p>
        )}
      </div>

      <AreYouSureModal
        showModal={showCancelModal}
        text={t("subscription.cancelConfirm") || "Are you sure you want to cancel your subscription?"}
        onClose={() => setShowCancelModal(false)}
        onConfirmed={handleConfirmCancelPlan}
        //title={t("subscription.cancelTitle")}
      />
    </div>
  );
};

export default SubscriptionSettings;
